import React from "react";
import { toast } from "react-toastify";
import { i18n } from "../translate/i18n";
import { isString } from "lodash";

// Determina o tema atual dos toasts (light/dark)
const getCurrentToastTheme = () => {
	try {
		const pref = window.localStorage.getItem("preferredTheme") || window.localStorage.getItem("themeChoice");
		return pref === "dark" ? "dark" : "light";
	} catch {
		return "light";
	}
};

// Componente visual do toast de sucesso
const SuccessToastContent = ({ icon, title, message, actionButton }) => {
	const theme = getCurrentToastTheme();
	const titleColor = theme === "dark" ? "#86efac" : "#166534";
	const textColor = theme === "dark" ? "#bbf7d0" : "#14532d";
	return (
		<div style={{
			display: 'flex',
			alignItems: 'flex-start',
			gap: '12px',
			textAlign: 'left'
		}}>
			<span style={{
				fontSize: '20px',
				flexShrink: 0,
				marginTop: '2px'
			}}>
				{icon}
			</span>
			<div style={{ flex: 1 }}>
				{title && (
					<div style={{
						fontWeight: 600,
						marginBottom: '6px',
						fontSize: '15px',
						color: titleColor
					}}>
						{title}
					</div>
				)}
				<div style={{
					fontSize: '14px',
					lineHeight: '1.4',
					color: textColor,
					marginBottom: actionButton ? '12px' : '0'
				}}>
					{message}
				</div>
				{actionButton && (
					<div>{actionButton}</div>
				)}
			</div>
		</div>
	);
};

// Função para criar botões de ação
const createActionButton = (text, onClick, isPrimary = true) => (
	<button 
		onClick={onClick}
		style={{
			background: isPrimary ? '#16a34a' : '#6b7280',
			color: 'white',
			border: 'none',
			padding: '8px 16px',
			borderRadius: '6px',
			fontSize: '13px',
			fontWeight: '500',
			cursor: 'pointer',
			transition: 'background 0.2s',
			marginRight: '8px'
		}}
		onMouseOver={(e) => e.target.style.background = isPrimary ? '#15803d' : '#4b5563'}
		onMouseOut={(e) => e.target.style.background = isPrimary ? '#16a34a' : '#6b7280'}
	>
		{text}
	</button>
);

const toastSuccess = (message, options = {}) => {
	// Configurações básicas melhoradas
	const getToastOptions = (autoCloseTime = 5000) => ({
		autoClose: autoCloseTime,
		hideProgressBar: false,
		closeOnClick: true,
		pauseOnHover: true,
		draggable: true,
		theme: getCurrentToastTheme(),
		bodyStyle: {
			margin: 0,
			padding: 0,
			fontSize: '14px'
		},
		style: {
			borderRadius: "8px",
			boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
			backgroundColor: getCurrentToastTheme() === 'dark' ? '#111827' : '#f0fdf4',
			borderLeft: "4px solid #16a34a",
			padding: "16px",
			minHeight: "70px",
		},
		progressStyle: {
			background: "#16a34a",
			height: "3px",
		},
	});

	// Detectar tipo de sucesso automaticamente baseado na mensagem
	let icon = "✅";
	let title = "Sucesso";
	let autoCloseTime = 4000;
	let toastId = "generic-success";

	if (isString(message)) {
		const msg = message.toLowerCase();
		
		// Login / Autenticação
		if (msg.includes('login') || msg.includes('bem-vindo')) {
			icon = "🎉";
			title = "Bem-vindo!";
			toastId = "login-success";
		}
		// Salvamento / Criação
		else if (msg.includes('salv') || msg.includes('criado') || msg.includes('criada') || msg.includes('adicionado') || msg.includes('adicionada')) {
			icon = "💾";
			title = "Dados Salvos";
			toastId = "save-success";
		}
		// Atualização
		else if (msg.includes('atualiz') || msg.includes('edit') || msg.includes('modific')) {
			icon = "🔄";
			title = "Dados Atualizados";
			toastId = "update-success";
		}
		// Exclusão
		else if (msg.includes('exclu') || msg.includes('delet') || msg.includes('removid') || msg.includes('removido')) {
			icon = "🗑️";
			title = "Item Removido";
			autoCloseTime = 3000;
			toastId = "delete-success";
		}
		// Envio / Enviar
		else if (msg.includes('enviad') || msg.includes('enviou') || msg.includes('envio')) {
			icon = "📤";
			title = "Enviado";
			autoCloseTime = 3000;
			toastId = "send-success";
		}
		// Conexão
		else if (msg.includes('conex') || msg.includes('conectad') || msg.includes('facebook') || msg.includes('whatsapp')) {
			icon = "🔗";
			title = "Conectado";
			toastId = "connection-success";
		}
		// Tags / Etiquetas
		else if (msg.includes('tag') || msg.includes('etiqueta')) {
			icon = "🏷️";
			title = "Tag Atualizada";
			toastId = "tag-success";
		}
		// Filas
		else if (msg.includes('fila')) {
			icon = "📋";
			title = "Fila Configurada";
			toastId = "queue-success";
		}
		// Usuários
		else if (msg.includes('usuário') || msg.includes('usuario')) {
			icon = "👤";
			title = "Usuário Configurado";
			toastId = "user-success";
		}
		// Agendamento
		else if (msg.includes('agendamento') || msg.includes('agendado')) {
			icon = "📅";
			title = "Agendamento Realizado";
			toastId = "schedule-success";
		}
		// Código / Reset
		else if (msg.includes('código') || msg.includes('codigo') || msg.includes('senha')) {
			icon = "🔑";
			title = "Código Enviado";
			toastId = "code-success";
		}
		// Configurações
		else if (msg.includes('configura') || msg.includes('horário') || msg.includes('horario')) {
			icon = "⚙️";
			title = "Configuração Salva";
			toastId = "settings-success";
		}
		// Mensagens
		else if (msg.includes('mensagem')) {
			icon = "💬";
			title = "Mensagem Enviada";
			autoCloseTime = 3000;
			toastId = "message-success";
		}
		// Fluxo
		else if (msg.includes('fluxo')) {
			icon = "🔄";
			title = "Fluxo Atualizado";
			toastId = "flow-success";
		}
		// Notificação
		else if (msg.includes('notificação') || msg.includes('notificacao')) {
			icon = "🔔";
			title = "Notificação Configurada";
			toastId = "notification-success";
		}
		// Importação / Upload
		else if (msg.includes('import') || msg.includes('upload') || msg.includes('arquivo')) {
			icon = "📁";
			title = "Arquivo Processado";
			toastId = "import-success";
		}
		// Cópia / Duplicação
		else if (msg.includes('copia') || msg.includes('duplicad') || msg.includes('clona')) {
			icon = "📋";
			title = "Item Copiado";
			toastId = "copy-success";
		}
	}

	// Usar tipo explícito se fornecido
	if (options.type) {
		switch (options.type) {
			case 'login':
				icon = "🎉"; title = "Bem-vindo!"; break;
			case 'save':
				icon = "💾"; title = "Dados Salvos"; break;
			case 'delete':
				icon = "🗑️"; title = "Item Removido"; autoCloseTime = 3000; break;
			case 'send':
				icon = "📤"; title = "Enviado"; autoCloseTime = 3000; break;
			case 'connection':
				icon = "🔗"; title = "Conectado"; break;
			case 'update':
				icon = "🔄"; title = "Atualizado"; break;
		}
	}

	// Prevenir toasts duplicados
	if (toast.isActive(toastId)) {
		return;
	}

	// Criar conteúdo
	const content = (
		<SuccessToastContent
			icon={icon}
			title={title}
			message={isString(message) ? message : "Operação realizada com sucesso"}
		/>
	);

	// Mostrar toast com conteúdo apropriado
	toast.success(content, { 
		...getToastOptions(autoCloseTime), 
		toastId: options.toastId || toastId 
	});
};

// Função para substituir globalmente toast.success
export const replaceToastSuccess = () => {
	// Salvar referência original
	const originalToastSuccess = toast.success;
	
	// Substituir toast.success pela nossa versão customizada
	toast.success = (message, options = {}) => {
		// Se já é um componente React, usar toast original
		if (React.isValidElement(message)) {
			return originalToastSuccess(message, options);
		}
		
		// Caso contrário, usar nossa versão customizada
		return toastSuccess(message, options);
	};
	
	console.log('✅ Toast Success padronizado globalmente');
};

export default toastSuccess;
